import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Alert, Button, Card } from "react-bootstrap";
import { defineMessages, FormattedMessage } from "react-intl";
import Selector from "../../components/selector";
import { useGrpcClient } from "../../lib/grpc";
import { PropertyAPIPromiseClient } from "../../gen/witbooking/backoffice/property/v1/property_api_grpc_web_pb";
import { ChannelManagerAPIPromiseClient } from "../../gen/witbooking/backoffice/channelmanager/v1/channelmanager_api_grpc_web_pb";
import { FullWidthLoader } from "../../components/full-width-loader";
import { SectionHeading } from "../../components/section-heading";
import { ACCESS_DENIED, getPropertyRpt } from "../../lib/auth";
import { useKeycloakAuthz } from "../../components/keycloak-provider";
import {
  GetChannelManagersRequest,
  GetPropertyChannelManagersRequest,
  UpdatePropertyChannelManagersRequest,
} from "../../gen/witbooking/backoffice/channelmanager/v1/channelmanager_api_pb";
import { defaultFormMessages } from "../shared/crud/form";
import { defaultListMessages } from "../shared/crud/list";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { useUserPropertyTickers } from "../../components/user-tickers-providers";

const messages = {
  ...defaultFormMessages,
  ...defaultListMessages,
  ...defineMessages({
    title: { id: "internalmenu.integrations.channelManager", defaultMessage: "Configure channels" },
    propertyTickerSelectPlaceholder: {
      id: "channelManager.placeholder.propertyTicker",
      defaultMessage: "Engine identifier",
    },
    channelsSelectPlaceholder: {
      id: "channelManager.placeholder.channels",
      defaultMessage: "Channel managers (multiple selection)",
    },
  }),
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1rem;
  > * {
    margin: 1rem 0;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 1rem;
`;
const Label = styled.div`
  font-size: 18px;
  margin-bottom: -10px;
`;

type Option = { value: string; label: string };

interface OperationError {
  message: string;
}

function ChannelManagerActivation() {
  const userPropertyTickersList = useUserPropertyTickers();
  const propertyGrpcClient = useGrpcClient(PropertyAPIPromiseClient);
  const channelManagerGrpcClient = useGrpcClient(ChannelManagerAPIPromiseClient);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<OperationError | null | undefined>(undefined);
  const [configSuccess, setConfigSuccess] = useState<boolean>(false);

  const [propertyTickerOptions, setPropertyTickerOptions] = useState<Option[]>([]);
  const [propertyTickerOption, setPropertyTickerOption] = useState<Option | null | undefined>(
    undefined
  );
  const [loadedPropertyChannels, setLoadedPropertyChannels] = useState<boolean>(false);
  const [propertyChannelOption, setPropertyChannelOption] = useState<Option[]>([]);
  const [allChannelsOptions, setAllChannelsOptions] = useState<Option[]>([]);

  const keycloakAuthz = useKeycloakAuthz();

  useEffect(() => {
    const fetchPropertyTickersAndChannels = async () => {
      setLoading(true);
      const [allChannels] = await Promise.all([
        channelManagerGrpcClient.getChannelManagers(new GetChannelManagersRequest()),
      ]);
      setPropertyTickerOptions(
        userPropertyTickersList.map(option => ({ value: option, label: option }))
      );
      setAllChannelsOptions(
        allChannels.getChannelManagersList().map(option => ({ value: option, label: option }))
      );
      setLoading(false);
    };
    fetchPropertyTickersAndChannels();
  }, [channelManagerGrpcClient, userPropertyTickersList]);

  useEffect(() => {
    const fetchPropertyChannels = async () => {
      if (!propertyTickerOption || !propertyTickerOption.value) {
        return;
      }
      setLoading(true);
      setError(undefined);
      setConfigSuccess(false);
      setLoadedPropertyChannels(false);
      try {
        const token = await getPropertyRpt(propertyTickerOption.value, keycloakAuthz);
        const propertyChannels = await channelManagerGrpcClient.getPropertyChannelManagers(
          new GetPropertyChannelManagersRequest(),
          {
            Authorization: `Bearer ${token}`,
          }
        );
        setLoadedPropertyChannels(true);
        setPropertyChannelOption(
          propertyChannels
            .getChannelManagersList()
            .map(option => ({ value: option, label: option }))
        );
      } catch (e) {
        setLoadedPropertyChannels(false);
        if (e === ACCESS_DENIED) {
          console.error("Access denied", e);
          setError({ message: "Access denied" });
        } else {
          console.error("Error retrieving children", e);
          setError({ message: "Error retrieving children" });
        }
      }
      setLoading(false);
    };
    fetchPropertyChannels();
  }, [propertyTickerOption, propertyGrpcClient, keycloakAuthz, channelManagerGrpcClient]);

  const handleSelectPropertyTicker = useCallback(
    selectedOption => setPropertyTickerOption(selectedOption),
    [setPropertyTickerOption]
  );

  const handleChangePropertyChannels = useCallback(
    selectedOption => setPropertyChannelOption(selectedOption || []),
    [setPropertyChannelOption]
  );

  const configureChannels = useCallback(async () => {
    if (!propertyTickerOption || !propertyTickerOption.value) {
      return;
    }
    setLoading(true);
    setError(undefined);
    setConfigSuccess(false);
    const token = await getPropertyRpt(propertyTickerOption.value, keycloakAuthz);
    const updatePropertyChannelManagersRequest = new UpdatePropertyChannelManagersRequest();
    updatePropertyChannelManagersRequest.setChannelManagersList(
      propertyChannelOption.map(it => it.value)
    );
    try {
      await channelManagerGrpcClient.updatePropertyChannelManagers(
        updatePropertyChannelManagersRequest,
        {
          Authorization: `Bearer ${token}`,
        }
      );
      setConfigSuccess(true);
    } catch (e) {
      setConfigSuccess(false);
      if (e === ACCESS_DENIED) {
        console.error("Access denied", e);
        setError({ message: "Access denied" });
      } else {
        console.error("Error retrieving children", e);
        setError({ message: "Error creating chain" });
      }
    }
    setLoading(false);
  }, [propertyTickerOption, propertyChannelOption, keycloakAuthz, channelManagerGrpcClient]);

  if (loading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Card className="ml-4 mr-4 mb-4 mt-4">
        <Container>
          <Label>
            <FormattedMessage {...messages.propertyTickerSelectPlaceholder} />
          </Label>
          <Selector
            options={propertyTickerOptions}
            value={propertyTickerOption}
            onChange={handleSelectPropertyTicker}
            placeholder={<FormattedMessage {...messages.propertyTickerSelectPlaceholder} />}
          />
          {loadedPropertyChannels && (
            <>
              <Label>
                <FormattedMessage {...messages.channelsSelectPlaceholder} />
              </Label>
              <Selector
                value={propertyChannelOption}
                options={allChannelsOptions}
                onChange={handleChangePropertyChannels}
                placeholder={<FormattedMessage {...messages.channelsSelectPlaceholder} />}
                isMulti
              />
            </>
          )}
        </Container>
        {error && (
          <Alert variant="danger">
            <strong>
              <FormattedMessage {...messages.saveError} />
            </strong>
          </Alert>
        )}
        {configSuccess && (
          <Alert variant="success">
            <strong>
              <FormattedMessage {...messages.saveSuccess} />
            </strong>
          </Alert>
        )}
        <ButtonContainer>
          <Button variant="primary" type="button" onClick={configureChannels}>
            <FormattedMessage {...messages.save} />
          </Button>
        </ButtonContainer>
      </Card>
    </>
  );
}

export default withAuthorizationVerifier(ChannelManagerActivation);
